/**
 * CompanySheet — Right-side detail sheet for a company
 *
 * Phase 2 redesign (card-based, 3-tab, single-save):
 *  - Single "Save All Changes" footer button (replaces per-section saves)
 *  - Dirty-state guard: click-away shows "Discard changes?" dialog
 *  - Test Connection available in read-only mode
 *  - Sync Now (incremental) + Re-sync Full (split button with confirmation)
 *  - Remove integration per row
 *
 * Integration types: HR, Project, Timesheet (scheduler-specific)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  Plus,
  X,
  UserRound,
  Search,
  PoundSterling,
  Users,
  FolderKanban,
  CalendarDays,
  Clock,
  ChevronDown,
  RefreshCw,
  Trash2,
  UserMinus,
  UserPlus,
  RotateCcw,
  Save,
  AlertTriangle,
  Copy,
  Lock,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DiscardChangesDialog } from '@/components/settings/DiscardChangesDialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import * as integrationsService from '@/services/integrationsService';
import { mockIntegrationsService } from '@/data/mockIntegrationsData';
import { useScheduler } from '@/contexts/SchedulerContext';
import { getApiBaseUrl } from '@/services/api';
import type { ExternalInstanceDto, AccessProjectsCompanyDto } from '@/services/api';
import {
  HR_PRODUCT_LABELS,
  PROJECT_PRODUCT_LABELS,
  TIMESHEET_PRODUCT_LABELS,
} from '@/types/integrations';
import type {
  CompanyWithIntegrations,
  CompanyUser,
  HRProduct,
  ProjectProduct,
  TimesheetProduct,
  IntegrationConnectionStatus,
  UpdateIntegrationsPayload,
} from '@/types/integrations';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CompanySheetProps {
  company: CompanyWithIntegrations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompanyUpdate: (
    updated: CompanyWithIntegrations,
    options?: { suppressEffects?: boolean },
  ) => void;
  mode?: 'view' | 'create';
  onCompanyCreated?: (created: CompanyWithIntegrations) => void;
  existingCodes?: string[];
  isDemo?: boolean;
  canManageIntegrations?: boolean;
  canViewIntegrations?: boolean;
}

/** Local draft state for a single integration row */
interface IntegrationDraft {
  product: string;
  instanceId: string;
  instanceName: string;
  secondaryId: string; // for access-projects
}

/** All draft state collected at the sheet level */
interface SheetDraft {
  companyName: string;
  companyCode: string;
  isActive: boolean;
  hr: IntegrationDraft;
  project: IntegrationDraft;
  timesheet: IntegrationDraft;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  return String(name ?? '')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function formatLastSync(lastSyncAt: string | null): string {
  if (!lastSyncAt) return 'Never';
  return new Date(lastSyncAt).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function RoleBadge({ role }: { role: string }) {
  const lower = role.toLowerCase();
  let className = 'border-muted-foreground/30 text-muted-foreground';

  if (lower.includes('admin') || lower.includes('manager') || lower.includes('director')) {
    className = 'border-blue-400 text-blue-700 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-300';
  } else if (lower.includes('lead') || lower.includes('senior')) {
    className = 'border-violet-400 text-violet-700 bg-violet-50 dark:bg-violet-950/30 dark:text-violet-300';
  } else if (lower.includes('finance') || lower.includes('account')) {
    className = 'border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300';
  } else if (lower.includes('hr') || lower.includes('people')) {
    className = 'border-pink-400 text-pink-700 bg-pink-50 dark:bg-pink-950/30 dark:text-pink-300';
  }

  return (
    <Badge variant="outline" className={cn('text-[10px] font-medium px-1.5 py-0 shrink-0', className)}>
      {role}
    </Badge>
  );
}

function ConnectionStatusBadge({ status }: { status: IntegrationConnectionStatus }) {
  switch (status) {
    case 'connected':
      return (
        <Badge variant="outline" className="border-success-500 text-success-700 bg-success-50 gap-1 text-xs">
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1 text-xs">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    case 'not_connected':
      return (
        <Badge variant="secondary" className="gap-1 text-xs">
          <XCircle className="h-3 w-3" />
          Not Connected
        </Badge>
      );
    case 'not_configured':
      return (
        <Badge variant="outline" className="text-muted-foreground gap-1 text-xs">
          Not Configured
        </Badge>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Integration health summary
// ---------------------------------------------------------------------------

function IntegrationHealthSummary({ company }: { company: CompanyWithIntegrations }) {
  const integrations = [
    { label: 'HR', status: company.hrIntegration.status },
    { label: 'Project', status: company.projectIntegration.status },
  ];

  const configured = integrations.filter((i) => i.status !== 'not_configured');
  const errors = configured.filter((i) => i.status === 'error');
  const notConnected = configured.filter((i) => i.status === 'not_connected');
  const connected = configured.filter((i) => i.status === 'connected');

  if (configured.length === 0) {
    return (
      <div className="flex items-center gap-2.5 rounded-md bg-muted/50 border px-3 py-2.5 text-sm text-muted-foreground">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>No integrations configured yet.</span>
      </div>
    );
  }
  if (errors.length > 0) {
    return (
      <div className="flex items-start gap-2.5 rounded-md bg-destructive/5 border border-destructive/20 px-3 py-2.5">
        <XCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
        <div>
          <p className="text-sm font-medium text-destructive">
            {errors.length} integration{errors.length > 1 ? 's' : ''} {errors.length > 1 ? 'have' : 'has'} an error
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {errors.map((e) => e.label).join(', ')} — update the configuration below
          </p>
        </div>
      </div>
    );
  }
  if (notConnected.length > 0) {
    return (
      <div className="flex items-start gap-2.5 rounded-md bg-warning-50 dark:bg-warning-950/20 border border-warning-200 dark:border-warning-800 px-3 py-2.5">
        <AlertCircle className="h-4 w-4 shrink-0 text-warning-600 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-warning-800 dark:text-warning-200">
            {notConnected.length} integration{notConnected.length > 1 ? 's' : ''} not connected
          </p>
          <p className="text-xs text-warning-600 dark:text-warning-400 mt-0.5">
            {notConnected.map((e) => e.label).join(', ')} — use Test Connection below to diagnose
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 rounded-md bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-3 py-2.5">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-success-600" />
      <p className="text-sm font-medium text-success-800 dark:text-success-200">
        {connected.length === configured.length
          ? `All ${connected.length} integration${connected.length > 1 ? 's' : ''} connected`
          : `${connected.length} of ${configured.length} integrations connected`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single integration row — card-based display
// ---------------------------------------------------------------------------

interface IntegrationRowProps {
  companyId: string;
  type: 'hr' | 'project' | 'timesheet';
  draft: IntegrationDraft;
  savedStatus: IntegrationConnectionStatus;
  savedLastSyncAt: string | null;
  onDraftChange: (type: 'hr' | 'project' | 'timesheet', draft: IntegrationDraft) => void;
  onSaved: (updated: CompanyWithIntegrations) => void;
  readOnly: boolean;
  isDemo: boolean;
}

function IntegrationRow({
  companyId,
  type,
  draft,
  savedStatus,
  savedLastSyncAt,
  onDraftChange,
  onSaved,
  readOnly,
  isDemo,
}: IntegrationRowProps) {
  const svc = isDemo ? mockIntegrationsService : integrationsService;
  const { refreshData } = useScheduler();
  const [instances, setInstances] = useState<ExternalInstanceDto[]>([]);
  const [isLoadingInstances, setIsLoadingInstances] = useState(false);
  const [noInstances, setNoInstances] = useState(false);

  const [projectCompanies, setProjectCompanies] = useState<AccessProjectsCompanyDto[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [noCompanies, setNoCompanies] = useState(false);

  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<boolean | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showResyncConfirm, setShowResyncConfirm] = useState(false);
  const [localLastSyncAt, setLocalLastSyncAt] = useState<string | null>(savedLastSyncAt);

  const [maskedApiSecret, setMaskedApiSecret] = useState<string | null>(null);
  const [apiSecretInput, setApiSecretInput] = useState('');
  const [isEditingApiSecret, setIsEditingApiSecret] = useState(false);
  const [isSavingApiSecret, setIsSavingApiSecret] = useState(false);

  const instancesLoadedForProduct = useRef<string>('');

  const productOptions =
    type === 'hr'
      ? (Object.entries(HR_PRODUCT_LABELS) as [HRProduct, string][]).filter(
          ([key]) => key === 'people-hr' || key === 'access-time-hr',
        )
      : type === 'project'
        ? ([['access-projects', PROJECT_PRODUCT_LABELS['access-projects']]] as [ProjectProduct, string][])
        : (Object.entries(TIMESHEET_PRODUCT_LABELS) as [TimesheetProduct, string][]);

  const label =
    type === 'hr' ? 'HR'
    : type === 'project' ? 'Project'
    : 'Timesheet';

  const icon =
    type === 'hr' ? <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    : type === 'project' ? <FolderKanban className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    : <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />;

  const requiresCompanySelection = draft.product === 'access-projects';

  // Load instances whenever product changes
  useEffect(() => {
    if (!draft.product || draft.product === instancesLoadedForProduct.current) return;
    instancesLoadedForProduct.current = draft.product;

    // For access-projects, load companies directly
    if (draft.product === 'access-projects') {
      loadProjectCompanies(draft.product);
      return;
    }

    // For access-time / access-time-hr, load databases
    // access-time-hr shares the same platform product as access-time
    if (draft.product === 'access-time' || draft.product === 'access-time-hr') {
      loadDatabases('access-time');
      return;
    }

    setIsLoadingInstances(true);
    setNoInstances(false);
    setInstances([]);
    svc.getExternalInstances(draft.product, companyId)
      .then((data) => {
        setInstances(data);
        if (data.length === 0) {
          setNoInstances(true);
        } else if (!draft.instanceId) {
          onDraftChange(type, {
            ...draft,
            instanceId: data[0].id,
            instanceName: data[0].name,
          });
        } else {
          const match = data.find((i) => i.id === draft.instanceId);
          if (match && match.name !== draft.instanceName) {
            onDraftChange(type, { ...draft, instanceName: match.name });
          }
        }
      })
      .catch(() => setNoInstances(true))
      .finally(() => setIsLoadingInstances(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.product]);

  // Sync local last sync when prop changes
  useEffect(() => {
    setLocalLastSyncAt(savedLastSyncAt);
  }, [savedLastSyncAt]);

  // Load masked API secret for people-hr
  useEffect(() => {
    if (draft.product !== 'people-hr') return;
    svc.getApiSecret(companyId, type)
      .then((res) => setMaskedApiSecret(res.maskedApiSecret))
      .catch(() => setMaskedApiSecret(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, type, savedStatus, draft.product]);

  async function loadProjectCompanies(productCode: string) {
    setIsLoadingCompanies(true);
    setNoCompanies(false);
    setProjectCompanies([]);
    try {
      const companies = await svc.getAccessProjectsCompanies({ product: productCode });
      setProjectCompanies(companies);
      if (companies.length === 0) {
        setNoCompanies(true);
      } else if (!draft.instanceId) {
        onDraftChange(type, {
          ...draft,
          instanceId: companies[0].id,
          instanceName: companies[0].name ?? companies[0].id,
          secondaryId: companies[0].id,
        });
      } else {
        const match = companies.find((c) => c.id === draft.instanceId);
        if (match && (match.name ?? match.id) !== draft.instanceName) {
          onDraftChange(type, {
            ...draft,
            instanceName: match.name ?? match.id,
            secondaryId: match.id,
          });
        }
      }
    } catch {
      setNoCompanies(true);
    } finally {
      setIsLoadingCompanies(false);
    }
  }

  async function loadDatabases(productCode: string) {
    setIsLoadingCompanies(true);
    setNoCompanies(false);
    setProjectCompanies([]);
    try {
      const databases = await svc.getAvailableDatabases(productCode);
      const mapped = databases.map((db) => ({ id: db.id, name: db.label }));
      setProjectCompanies(mapped);
      if (mapped.length === 0) {
        setNoCompanies(true);
      } else if (!draft.instanceId) {
        onDraftChange(type, {
          ...draft,
          instanceId: mapped[0].id,
          instanceName: mapped[0].name,
        });
      } else {
        const match = mapped.find((db) => db.id === draft.instanceId);
        if (match && match.name !== draft.instanceName) {
          onDraftChange(type, {
            ...draft,
            instanceName: match.name,
          });
        }
      }
    } catch {
      setNoCompanies(true);
    } finally {
      setIsLoadingCompanies(false);
    }
  }

  function handleProductChange(value: string) {
    instancesLoadedForProduct.current = '';
    onDraftChange(type, {
      product: value,
      instanceId: '',
      instanceName: '',
      secondaryId: '',
    });
    setInstances([]);
    setNoInstances(false);
    setProjectCompanies([]);
    setNoCompanies(false);
    setTestResult(null);
  }

  function handleInstanceChange(name: string) {
    if (draft.product === 'access-projects' || draft.product === 'access-time' || draft.product === 'access-time-hr') {
      const company = projectCompanies.find((c) => (c.name ?? c.id) === name);
      onDraftChange(type, {
        ...draft,
        instanceId: company?.id ?? '',
        instanceName: name,
        secondaryId: company?.id ?? '',
      });
    } else {
      const instance = instances.find((i) => i.name === name);
      onDraftChange(type, { ...draft, instanceId: instance?.id ?? '', instanceName: name });
    }
  }

  // Save API secret (for People HR)
  async function handleSaveApiSecret() {
    if (!apiSecretInput.trim()) return;
    setIsSavingApiSecret(true);
    try {
      const result = await svc.saveApiSecret(companyId, type, apiSecretInput);
      setMaskedApiSecret(result.maskedApiSecret);
      setApiSecretInput('');
      setIsEditingApiSecret(false);
      toast.success('Api Secret saved');
    } catch {
      toast.error('Failed to save Api Secret');
    } finally {
      setIsSavingApiSecret(false);
    }
  }

  // Delete API secret (for People HR)
  async function handleDeleteApiSecret() {
    try {
      await svc.deleteApiSecret(companyId, type);
      setMaskedApiSecret(null);
      setApiSecretInput('');
      setIsEditingApiSecret(false);
      toast.success('Api Secret deleted');
    } catch {
      toast.error('Failed to delete Api Secret');
    }
  }

  // Test connection
  async function handleTest() {
    setIsTesting(true);
    try {
      const result = await svc.testConnection(companyId, type);
      setTestResult(result.success);
      if (result.success) {
        toast.success('Connection successful', { description: result.message });
      } else {
        toast.error('Connection failed', { description: result.message });
      }
      try {
        const refreshed = await svc.getCompany(companyId);
        if (refreshed) onSaved(refreshed);
      } catch { /* non-fatal */ }
    } catch {
      toast.error('Test connection failed');
      setTestResult(false);
    } finally {
      setIsTesting(false);
    }
  }

  // Incremental sync
  async function handleSync() {
    setIsSyncing(true);
    try {
      const result = await svc.syncIntegration(companyId, type);
      if (result.success) {
        if (result.syncedAt) setLocalLastSyncAt(result.syncedAt);
        if (result.updatedCompany) onSaved(result.updatedCompany);
        toast.success('Sync complete', { description: result.message });
        // Data fetch happens on Sync Now (not on save): refresh all scheduler
        // data (resources, clients, cost centres, projects, scheduler groups,
        // tab/field configurations, colour overrides, etc.) now that fresh data
        // exists. Run it after reporting the outcome and swallow its errors so a
        // refresh problem never hides a successful sync.
        void refreshData().catch((err) => {
          console.warn('Post-sync scheduler data refresh failed:', err);
        });
      } else {
        toast.error('Sync failed', { description: result.message });
      }
    } catch {
      toast.error('Sync failed — please try again');
    } finally {
      setIsSyncing(false);
    }
  }

  // Full re-sync
  async function handleResync() {
    setIsResyncing(true);
    setShowResyncConfirm(false);
    try {
      const result = await svc.resyncIntegration(companyId, type);
      if (result.success) {
        if (result.syncedAt) setLocalLastSyncAt(result.syncedAt);
        if (result.updatedCompany) onSaved(result.updatedCompany);
        toast.success('Full re-sync complete', { description: result.message });
        void refreshData().catch((err) => {
          console.warn('Post-re-sync scheduler data refresh failed:', err);
        });
      } else {
        toast.error('Re-sync failed', { description: result.message });
      }
    } catch {
      toast.error('Re-sync failed — please try again');
    } finally {
      setIsResyncing(false);
    }
  }

  async function handleRemove() {
    setIsRemoving(true);
    try {
      const updated = await svc.removeIntegration(companyId, type);
      if (updated) {
        onSaved(updated);
        onDraftChange(type, { product: '', instanceId: '', instanceName: '', secondaryId: '' });
        setInstances([]);
        setLocalLastSyncAt(null);
        setTestResult(null);
        instancesLoadedForProduct.current = '';
        toast.success(`${label} integration removed`);
      }
    } catch {
      toast.error('Failed to remove integration');
    } finally {
      setIsRemoving(false);
      setShowRemoveConfirm(false);
    }
  }

  const isConfigured = savedStatus !== 'not_configured';
  const canTestOrSync = !!draft.product && !!draft.instanceId;

  // Determine which list/loading state to use for instance/company dropdown
  const useCompanyDropdown = draft.product === 'access-projects' || draft.product === 'access-time' || draft.product === 'access-time-hr';

  return (
    <>
      <div className="rounded-lg border bg-card p-4 space-y-3">
        {/* Row header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm font-semibold">{label} Integration</span>
          </div>
          <ConnectionStatusBadge status={savedStatus} />
        </div>

        {/* Normal integration row */}
        <div className="space-y-3">
          {/* Product */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Product</Label>
            {readOnly ? (
              <p className="text-sm py-1">
                {draft.product
                  ? (productOptions.find(([v]) => v === draft.product)?.[1] ?? draft.product)
                  : <span className="italic text-muted-foreground">Not configured</span>}
              </p>
            ) : (
              <Select value={draft.product} onValueChange={handleProductChange}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select product…" />
                </SelectTrigger>
                <SelectContent>
                  {productOptions.map(([value, lbl]) => (
                    <SelectItem key={value} value={value}>{lbl}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Instance Name (non-company-selection products) */}
          {draft.product && !useCompanyDropdown && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Instance</Label>
              {readOnly ? (
                <p className="text-sm py-1">
                  {draft.instanceName || <span className="italic text-muted-foreground">&mdash;</span>}
                </p>
              ) : isLoadingInstances ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading instances…
                </div>
              ) : noInstances ? (
                <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2">
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  <span className="text-xs text-destructive">
                    No instances found for this product.
                  </span>
                </div>
              ) : (
                <Select value={draft.instanceName} onValueChange={handleInstanceChange}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select instance…" />
                  </SelectTrigger>
                  <SelectContent>
                    {instances.map((i) => (
                      <SelectItem key={i.id} value={i.name}>
                        {i.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Company dropdown for access-projects / access-time */}
          {draft.product && useCompanyDropdown && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Company</Label>
              {readOnly ? (
                <p className="text-sm py-1">
                  {draft.instanceName || <span className="italic text-muted-foreground">&mdash;</span>}
                </p>
              ) : isLoadingInstances || isLoadingCompanies ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading companies…
                </div>
              ) : noInstances || noCompanies ? (
                <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2">
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  <span className="text-xs text-destructive">
                    No companies found. Unable to configure this product.
                  </span>
                </div>
              ) : projectCompanies.length > 0 ? (
                <Select value={draft.instanceName} onValueChange={handleInstanceChange}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select a company" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.name ?? company.id}>
                        {company.name ?? company.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          )}

          {/* Webhook URL for People HR */}
          {draft.product === 'people-hr' && draft.instanceId && !isLoadingInstances && !noInstances && (() => {
            const base = getApiBaseUrl();
            const baseWithApi = base.endsWith('/api') ? base : `${base}/api`;
            const organisationId = sessionStorage.getItem('evo_organisation_id') || '';
            const webhookUrl = `${baseWithApi}/peoplehr/webhook/${organisationId}`;
            return (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Webhook URL</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={webhookUrl}
                    className="h-8 text-sm bg-muted cursor-default"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 flex-shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(webhookUrl);
                      toast.success('Webhook URL copied to clipboard');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })()}

          {/* Api Secret field — shown only for People HR */}
          {draft.product === 'people-hr' && draft.instanceId && !isLoadingInstances && !noInstances && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Api Secret</Label>
              {readOnly ? (
                <p className="text-sm py-1 font-mono">
                  {maskedApiSecret ?? <span className="italic text-muted-foreground">—</span>}
                </p>
              ) : isEditingApiSecret ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    value={apiSecretInput}
                    onChange={(e) => setApiSecretInput(e.target.value)}
                    placeholder="Enter Api Secret"
                    className="h-8 text-sm"
                  />
                  <Button
                    variant="default"
                    size="sm"
                    className="h-8 px-3 flex-shrink-0"
                    onClick={handleSaveApiSecret}
                    disabled={isSavingApiSecret || !apiSecretInput.trim()}
                  >
                    {isSavingApiSecret ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 flex-shrink-0"
                    onClick={() => { setIsEditingApiSecret(false); setApiSecretInput(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={maskedApiSecret ?? ''}
                    placeholder="Not set"
                    className="h-8 text-sm bg-muted cursor-default font-mono"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 flex-shrink-0"
                    onClick={() => setIsEditingApiSecret(true)}
                  >
                    {maskedApiSecret ? 'Edit' : 'Set'}
                  </Button>
                  {maskedApiSecret && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 flex-shrink-0 text-destructive hover:text-destructive"
                      onClick={handleDeleteApiSecret}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Last sync */}
          {localLastSyncAt && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3" />
              Last synced: <span className="font-medium text-foreground">{formatLastSync(localLastSyncAt)}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {/* Test Connection — only when not read-only */}
            {!readOnly && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={!canTestOrSync ? 0 : -1}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={handleTest}
                      disabled={isTesting || !canTestOrSync}
                    >
                      {isTesting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                      Test Connection
                    </Button>
                  </span>
                </TooltipTrigger>
                {!canTestOrSync && (
                  <TooltipContent side="bottom" className="text-xs max-w-48 text-center">
                    {!draft.product ? 'Select a product first' : 'Select an instance first'}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            )}

            {/* Test result inline */}
            {testResult !== null && (
              <span className={cn('text-xs font-medium flex items-center gap-1', testResult ? 'text-success-600' : 'text-destructive')}>
                {testResult ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                {testResult ? 'Pass' : 'Failed'}
              </span>
            )}

            {/* Sync Now + Re-sync — only when connected and not read-only */}
            {!readOnly && savedStatus === 'connected' && (
              <div className="flex items-stretch gap-0 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 rounded-r-none border-r-0"
                  onClick={handleSync}
                  disabled={isSyncing || isResyncing}
                >
                  {isSyncing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                  {isSyncing ? 'Syncing…' : 'Sync Now'}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 rounded-l-none px-1.5"
                      disabled={isSyncing || isResyncing}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem
                      className="text-xs gap-2 text-muted-foreground"
                      onClick={() => setShowResyncConfirm(true)}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Full Re-sync…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Remove — only when configured and not read-only */}
            {!readOnly && isConfigured && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowRemoveConfirm(true)}
                disabled={isRemoving}
              >
                {isRemoving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Remove confirm */}
      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {label} Integration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect and remove all configuration for the {label.toLowerCase()} integration.
              Any active sync schedule will be cancelled. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Removing…</> : 'Remove Integration'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-sync confirm */}
      <AlertDialog open={showResyncConfirm} onOpenChange={setShowResyncConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning-600" />
              Full Re-sync {label} Integration?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  A full re-sync rebuilds all data from scratch. This is different from a standard sync which only picks up recent changes.
                </p>
                <ul className="list-disc pl-4 space-y-1 text-xs">
                  <li>May take several minutes to complete</li>
                  <li>Places higher load on the connected database</li>
                  <li>All incremental changes since the last sync will be reprocessed</li>
                </ul>
                <p className="text-xs font-medium text-foreground">
                  Only use this if you suspect data is out of sync. For routine updates, use Sync Now instead.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResync} className="bg-warning-600 hover:bg-warning-700 text-white">
              {isResyncing ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Re-syncing…</> : 'Start Full Re-sync'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Add Users Dialog — browsable multi-select modal
// ---------------------------------------------------------------------------

interface AddUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyWithIntegrations;
  onUsersSelected: (users: CompanyUser[]) => void;
  isDemo: boolean;
  pendingAdditionIds?: Set<string>;
}

function AddUsersDialog({ open, onOpenChange, company, onUsersSelected, isDemo, pendingAdditionIds }: AddUsersDialogProps) {
  const svc = isDemo ? mockIntegrationsService : integrationsService;
  const [allUsers, setAllUsers] = useState<CompanyUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [dialogSearch, setDialogSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setDialogSearch('');
    setSelectedIds(new Set());
    setIsLoadingUsers(true);
    svc.fetchAllUsers(company.id)
      .then(setAllUsers)
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setIsLoadingUsers(false));
  }, [open, company.id]);

  const filteredDialogUsers = dialogSearch.trim()
    ? allUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(dialogSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(dialogSearch.toLowerCase()) ||
          u.role.toLowerCase().includes(dialogSearch.toLowerCase())
      )
    : allUsers;

  // Filter out users already pending addition
  const availableDialogUsers = pendingAdditionIds?.size
    ? filteredDialogUsers.filter((u) => !pendingAdditionIds.has(u.id))
    : filteredDialogUsers;

  const allFilteredSelected =
    availableDialogUsers.length > 0 &&
    availableDialogUsers.every((u) => selectedIds.has(u.id));
  const someFilteredSelected =
    availableDialogUsers.some((u) => selectedIds.has(u.id)) && !allFilteredSelected;

  function toggleUser(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        availableDialogUsers.forEach((u) => next.delete(u.id));
      } else {
        availableDialogUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  }

  function handleConfirmAdd() {
    if (selectedIds.size === 0) return;
    const selected = allUsers.filter((u) => selectedIds.has(u.id));
    onUsersSelected(selected);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
        <DialogHeader className="px-5 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            Add Users to {company.name}
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            Select one or more users to grant access to this company.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="px-4 py-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="Search by name, email, role…"
              value={dialogSearch}
              onChange={(e) => setDialogSearch(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Select all header */}
        {!isLoadingUsers && availableDialogUsers.length > 0 && (
          <div
            className="flex items-center gap-3 px-4 py-2 bg-muted/40 border-b shrink-0 cursor-pointer hover:bg-muted/60 transition-colors"
            onClick={toggleAllFiltered}
          >
            <Checkbox
              checked={allFilteredSelected}
              data-state={someFilteredSelected ? 'indeterminate' : undefined}
              onCheckedChange={toggleAllFiltered}
              className="pointer-events-none"
            />
            <span className="text-xs font-medium text-muted-foreground">
              {allFilteredSelected
                ? 'Deselect all'
                : someFilteredSelected
                ? `${selectedIds.size} selected — select all ${availableDialogUsers.length}`
                : `Select all ${availableDialogUsers.length} user${availableDialogUsers.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}

        {/* User list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingUsers ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : availableDialogUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-2 px-4">
              {dialogSearch ? (
                <>
                  <Search className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No users match "{dialogSearch}"</p>
                </>
              ) : (
                <>
                  <Users className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">All users already have access</p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {availableDialogUsers.map((user) => {
                const isSelected = selectedIds.has(user.id);
                return (
                  <div
                    key={user.id}
                    className={cn(
                      'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'
                    )}
                    onClick={() => toggleUser(user.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleUser(user.id)}
                      className="pointer-events-none shrink-0"
                    />
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium flex-shrink-0">
                      {getInitials(user.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{user.role}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-4 py-3 border-t shrink-0 gap-2 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirmAdd}
            disabled={selectedIds.size === 0}
            className="gap-1.5"
          >
            <UserPlus className="h-3.5 w-3.5" />
            {selectedIds.size > 0
              ? `Add ${selectedIds.size} User${selectedIds.size !== 1 ? 's' : ''}`
              : 'Add Users'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bulk Remove Bar — floats at bottom of sheet when users are selected
// ---------------------------------------------------------------------------

interface BulkRemoveBarProps {
  selectedCount: number;
  onRemove: () => void;
  onClear: () => void;
  isRemoving: boolean;
}

function BulkRemoveBar({ selectedCount, onRemove, onClear, isRemoving }: BulkRemoveBarProps) {
  if (selectedCount === 0) return null;
  return (
    <div className="sticky bottom-0 left-0 right-0 z-10 pointer-events-none">
      <div className="flex justify-center pb-2">
        <div className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full bg-[#1a7f6e] text-white shadow-2xl text-sm whitespace-nowrap animate-in slide-in-from-bottom-4 duration-300">
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white/25 text-xs font-bold px-1">
            {selectedCount}
          </span>
          <span className="font-medium pr-1">
            {selectedCount === 1 ? 'user' : 'users'} selected
          </span>
          <div className="h-4 w-px bg-white/30" />
          <button
            onClick={onRemove}
            disabled={isRemoving}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors font-medium disabled:opacity-60"
          >
            {isRemoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserMinus className="h-3.5 w-3.5" />
            )}
            Remove Access
          </button>
          <div className="h-4 w-px bg-white/30" />
          <button
            onClick={onClear}
            className="flex items-center gap-1 text-white/80 hover:text-white transition-colors px-1"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
            <span className="text-xs">Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users with Access section
// ---------------------------------------------------------------------------

interface UsersWithAccessSectionProps {
  company: CompanyWithIntegrations;
  onUserCountChange: (count: number) => void;
  isDemo: boolean;
  canManage?: boolean;
  pendingAdditions: CompanyUser[];
  pendingRemovals: Set<string>;
  onPendingAdditionsChange: (users: CompanyUser[]) => void;
  onPendingRemovalsChange: (ids: Set<string>) => void;
  refreshKey?: number;
}

function UsersWithAccessSection({ company, onUserCountChange, isDemo, canManage = true, pendingAdditions, pendingRemovals, onPendingAdditionsChange, onPendingRemovalsChange, refreshKey = 0 }: UsersWithAccessSectionProps) {
  const svc = isDemo ? mockIntegrationsService : integrationsService;
  const [savedUsers, setSavedUsers] = useState<CompanyUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterQuery, setFilterQuery] = useState('');

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Add Users dialog
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Bulk remove state
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await svc.getCompanyUsers(company.id);
      setSavedUsers(data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id]);

  useEffect(() => {
    loadUsers();
    setSelectedIds(new Set());
  }, [loadUsers, refreshKey]);

  // Combine saved users (minus pending removals) with pending additions
  const users = [
    ...savedUsers.filter((u) => !pendingRemovals.has(u.id)),
    ...pendingAdditions,
  ];

  // Update parent user count whenever combined list changes
  useEffect(() => {
    onUserCountChange(users.length);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users.length]);

  const pendingAdditionIds = new Set(pendingAdditions.map((u) => u.id));

  const filteredUsers = filterQuery.trim()
    ? users.filter(
        (u) =>
          u.name.toLowerCase().includes(filterQuery.toLowerCase()) ||
          u.role.toLowerCase().includes(filterQuery.toLowerCase())
      )
    : users;

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.has(u.id));
  const someFilteredSelected =
    filteredUsers.some((u) => selectedIds.has(u.id)) && !allFilteredSelected;

  function toggleUser(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredUsers.forEach((u) => next.delete(u.id));
      } else {
        filteredUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  }

  function handleBulkRemove() {
    const ids = Array.from(selectedIds);
    // Separate pending additions from saved users
    const additionIdsToUndo = ids.filter((id) => pendingAdditionIds.has(id));
    const savedIdsToRemove = ids.filter((id) => !pendingAdditionIds.has(id));

    // Remove from pending additions
    if (additionIdsToUndo.length > 0) {
      const undoSet = new Set(additionIdsToUndo);
      onPendingAdditionsChange(pendingAdditions.filter((u) => !undoSet.has(u.id)));
    }

    // Mark saved users for pending removal
    if (savedIdsToRemove.length > 0) {
      const next = new Set(pendingRemovals);
      savedIdsToRemove.forEach((id) => next.add(id));
      onPendingRemovalsChange(next);
    }

    setSelectedIds(new Set());
    setShowBulkConfirm(false);
  }

  function handleUsersSelected(selected: CompanyUser[]) {
    onPendingAdditionsChange([...pendingAdditions, ...selected]);
  }

  const selectedCount = selectedIds.size;

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar: filter search + Add Users button */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8 h-8 text-sm"
            placeholder="Filter users…"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1 shrink-0"
          onClick={() => setIsAddDialogOpen(true)}
          disabled={!canManage}
        >
          <UserPlus className="h-3 w-3" />
          Add Users
        </Button>
      </div>

      {/* Select-all header row */}
      {!isLoading && filteredUsers.length > 0 && (
        <div
          className={cn(
            'flex items-center gap-3 px-2 py-1.5 rounded-md bg-muted/40 border transition-colors',
            canManage ? 'cursor-pointer hover:bg-muted/60' : 'cursor-not-allowed opacity-60'
          )}
          onClick={canManage ? toggleAllFiltered : undefined}
        >
          <Checkbox
            checked={allFilteredSelected}
            data-state={someFilteredSelected ? 'indeterminate' : undefined}
            onCheckedChange={toggleAllFiltered}
            disabled={!canManage}
            className="pointer-events-none"
          />
          <span className="text-xs text-muted-foreground font-medium flex-1">
            {allFilteredSelected
              ? 'Deselect all'
              : someFilteredSelected
              ? `${selectedCount} of ${filteredUsers.length} selected`
              : filterQuery.trim()
              ? `Select all ${filteredUsers.length} matching`
              : `Select all ${filteredUsers.length} users`}
          </span>
          {!isLoading && (
            <span className="text-xs text-muted-foreground">
              {filterQuery.trim()
                ? `${filteredUsers.length} of ${users.length}`
                : `${users.length} user${users.length !== 1 ? 's' : ''}`}
            </span>
          )}
        </div>
      )}

      {/* User list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 py-1 px-2">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1 flex-1">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
          <UserRound className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users assigned yet.</p>
          <p className="text-xs text-muted-foreground">
            Use "Add Users" to grant access to this company.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-1 gap-1.5 text-xs"
            onClick={() => setIsAddDialogOpen(true)}
            disabled={!canManage}
          >
            <UserPlus className="h-3.5 w-3.5" />
            Add Users
          </Button>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center gap-1">
          <Search className="h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No users match "{filterQuery}"</p>
        </div>
      ) : (
        <div className="divide-y rounded-md border overflow-hidden">
          {filteredUsers.map((user) => {
            const isSelected = selectedIds.has(user.id);
            const isPending = pendingAdditionIds.has(user.id);
            return (
              <div
                key={user.id}
                className={cn(
                  'flex items-center gap-3 py-2.5 px-3 transition-colors group',
                  canManage ? 'cursor-pointer' : 'cursor-not-allowed',
                  isSelected ? 'bg-primary/5' : isPending ? 'bg-success-50/50 dark:bg-success-950/20' : canManage ? 'hover:bg-muted/40' : ''
                )}
                onClick={canManage ? () => toggleUser(user.id) : undefined}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleUser(user.id)}
                  disabled={!canManage}
                  className="pointer-events-none shrink-0"
                />
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium flex-shrink-0">
                  {getInitials(user.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-tight truncate">{user.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email ?? user.role}</div>
                </div>
                {isPending && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-success-300 text-success-700 dark:border-success-700 dark:text-success-400 shrink-0">
                    New
                  </Badge>
                )}
                <RoleBadge role={user.role} />
              </div>
            );
          })}
        </div>
      )}

      {/* Read-only note */}
      {!isLoading && (
        <div className="flex items-center justify-between pt-1 border-t gap-2">
          <p className="text-xs text-muted-foreground">
            To manage a user's full profile and company access, go to Team Members.
          </p>
        </div>
      )}

      {/* Bulk Remove floating bar */}
      <BulkRemoveBar
        selectedCount={selectedCount}
        onRemove={() => setShowBulkConfirm(true)}
        onClear={() => setSelectedIds(new Set())}
        isRemoving={false}
      />

      {/* Add Users dialog */}
      <AddUsersDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        company={company}
        onUsersSelected={handleUsersSelected}
        isDemo={isDemo}
        pendingAdditionIds={pendingAdditionIds}
      />

      {/* Bulk remove confirmation */}
      <AlertDialog open={showBulkConfirm} onOpenChange={(o) => { if (!o) setShowBulkConfirm(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedCount} user{selectedCount !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedCount === 1
                ? 'This user will lose access to '
                : `These ${selectedCount} users will lose access to `}
              <strong>{company.name}</strong>. They will no longer see this company in their
              switcher.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Company Form
// ---------------------------------------------------------------------------

interface CreateCompanyFormProps {
  existingCodes: string[];
  onCreated: (company: CompanyWithIntegrations) => void;
  onCancel: () => void;
  isDemo?: boolean;
}

function CreateCompanyForm({ existingCodes, onCreated, onCancel, isDemo }: CreateCompanyFormProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; code?: string }>({});

  function validate() {
    const e: { name?: string; code?: string } = {};
    if (!name.trim() || name.trim().length < 2) e.name = 'Company name must be at least 2 characters';
    if (!code.trim() || code.trim().length < 2) e.code = 'Company code must be at least 2 characters';
    else if (code.trim().length > 20) e.code = 'Company code must be at most 20 characters';
    else if (!/^[A-Z0-9-]+$/.test(code.trim())) e.code = 'Uppercase letters, numbers, and hyphens only';
    else if (existingCodes.includes(code.trim().toUpperCase())) e.code = 'This company code is already in use';
    return e;
  }

  async function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setIsSaving(true);
    try {
      const created = isDemo
        ? await mockIntegrationsService.createCompany({ name: name.trim(), code: code.trim().toUpperCase(), status })
        : await integrationsService.createCompany({ name: name.trim(), code: code.trim().toUpperCase(), status });
      toast.success(`${created.name} created`);
      onCreated(created);
    } catch {
      toast.error('Failed to create company');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="px-6 py-5 space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="new-company-name">Company Name <span className="text-destructive">*</span></Label>
        <Input
          id="new-company-name"
          value={name}
          placeholder="e.g., UK Operations"
          onChange={(e) => { setName(e.target.value); setErrors((prev) => ({ ...prev, name: undefined })); }}
          disabled={isSaving}
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="new-company-code">Company Code <span className="text-destructive">*</span></Label>
        <Input
          id="new-company-code"
          value={code}
          placeholder="e.g., UK-OPS"
          className="uppercase"
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setErrors((prev) => ({ ...prev, code: undefined })); }}
          disabled={isSaving}
        />
        <p className="text-xs text-muted-foreground">Unique identifier — uppercase letters, numbers and hyphens only</p>
        {errors.code && <p className="text-xs text-destructive">{errors.code}</p>}
      </div>

      <div className="flex items-center justify-between py-1">
        <div>
          <Label htmlFor="new-company-status" className="text-sm font-medium">Active</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Inactive companies are hidden from the company switcher</p>
        </div>
        <Switch
          id="new-company-status"
          checked={status === 'active'}
          onCheckedChange={(checked) => setStatus(checked ? 'active' : 'inactive')}
          disabled={isSaving}
        />
      </div>

      <Separator />

      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button className="flex-1" onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          Create Company
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main CompanySheet
// ---------------------------------------------------------------------------

function buildEmptyDraft(): IntegrationDraft {
  return { product: '', instanceId: '', instanceName: '', secondaryId: '' };
}

function buildDraftFromCompany(company: CompanyWithIntegrations): SheetDraft {
  return {
    companyName: company.name,
    companyCode: company.code,
    isActive: company.status === 'active',
    hr: {
      product: company.hrIntegration.product ?? '',
      instanceId: company.hrIntegration.externalInstanceId ?? '',
      instanceName: company.hrIntegration.instanceName ?? '',
      secondaryId: '',
    },
    project: {
      product: company.projectIntegration.product ?? '',
      instanceId: company.projectIntegration.externalInstanceId ?? '',
      instanceName: company.projectIntegration.instanceName ?? '',
      secondaryId: company.projectIntegration.externalInstanceIdSecondary ?? '',
    },
    timesheet: {
      product: company.timesheetIntegration.product ?? '',
      instanceId: company.timesheetIntegration.externalInstanceId ?? '',
      instanceName: company.timesheetIntegration.instanceName ?? '',
      secondaryId: company.timesheetIntegration.externalInstanceIdSecondary ?? '',
    },
  };
}

export function CompanySheet({
  company,
  open,
  onOpenChange,
  onCompanyUpdate,
  mode = 'view',
  onCompanyCreated,
  existingCodes = [],
  isDemo = false,
  canManageIntegrations: canManageIntegrationsProp = true,
  canViewIntegrations: canViewIntegrationsProp = true,
}: CompanySheetProps) {
  const [draft, setDraft] = useState<SheetDraft | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [activeCompany, setActiveCompany] = useState<CompanyWithIntegrations | null>(company);

  // Pending user changes (deferred until Save All Changes)
  const [pendingUserAdditions, setPendingUserAdditions] = useState<CompanyUser[]>([]);
  const [pendingUserRemovals, setPendingUserRemovals] = useState<Set<string>>(new Set());

  // Incremented after saving user changes to trigger a re-fetch in UsersWithAccessSection
  const [userRefreshKey, setUserRefreshKey] = useState(0);

  const hasUserChanges = pendingUserAdditions.length > 0 || pendingUserRemovals.size > 0;

  // Initialise / reset draft when company or open state changes
  useEffect(() => {
    if (open && company) {
      setDraft(buildDraftFromCompany(company));
      setActiveCompany(company);
      setIsDirty(false);
      setPendingUserAdditions([]);
      setPendingUserRemovals(new Set());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company?.id]);

  // Keep activeCompany in sync when parent updates it
  useEffect(() => {
    if (company) setActiveCompany(company);
  }, [company]);

  // Intercept sheet close — show discard dialog if dirty
  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && (isDirty || hasUserChanges)) {
      setShowDiscardDialog(true);
      return;
    }
    onOpenChange(nextOpen);
  }

  function handleDiscard() {
    setShowDiscardDialog(false);
    setIsDirty(false);
    setPendingUserAdditions([]);
    setPendingUserRemovals(new Set());
    onOpenChange(false);
  }

  function updateDraftField(key: keyof SheetDraft, value: unknown) {
    setDraft((prev) => prev ? { ...prev, [key]: value } : prev);
    setIsDirty(true);
  }

  function handleIntegrationDraftChange(
    type: 'hr' | 'project' | 'timesheet',
    newDraft: IntegrationDraft
  ) {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, [type]: newDraft };
    });
    setIsDirty(true);
  }

  // Single save — commits all changes at once
  async function handleSaveAll() {
    if (!draft || !activeCompany) return;
    setIsSaving(true);
    const svc = isDemo ? mockIntegrationsService : integrationsService;
    try {
      // 1. Save company details if changed
      const detailsChanged =
        draft.companyName !== activeCompany.name ||
        draft.companyCode !== activeCompany.code ||
        (draft.isActive ? 'active' : 'inactive') !== activeCompany.status;

      // Capture optimistic user count before API calls may overwrite it
      const currentUserCount = activeCompany.userCount;

      // Track the latest company state through async operations so we don't
      // overwrite integration updates with stale closure data.
      let latestCompany: CompanyWithIntegrations = activeCompany;

      if (detailsChanged) {
        const updatedCompany = await svc.updateCompany(activeCompany.id, {
          name: draft.companyName,
          code: draft.companyCode.toUpperCase(),
          status: draft.isActive ? 'active' : 'inactive',
        });
        if (updatedCompany) {
          const preserved = { ...updatedCompany, userCount: currentUserCount };
          latestCompany = preserved;
          setActiveCompany(preserved);
          onCompanyUpdate(preserved, { suppressEffects: true });
        }
      }

      // 2. Save integrations
      if (isDemo) {
        const updated = await mockIntegrationsService.updateIntegrations(
          activeCompany.id,
          { product: null, externalInstanceId: null },
          {
            product: draft.hr.product || null,
            externalInstanceId: draft.hr.instanceId || null,
          },
          {
            product: draft.project.product || null,
            externalInstanceId: draft.project.instanceId || null,
          },
          { product: null, externalInstanceId: null },
          {
            product: draft.timesheet.product || null,
            externalInstanceId: draft.timesheet.instanceId || null,
          },
        );
        if (updated) {
          const preserved = { ...updated, userCount: currentUserCount };
          latestCompany = preserved;
          setActiveCompany(preserved);
          onCompanyUpdate(preserved, { suppressEffects: true });
          setDraft(buildDraftFromCompany(preserved));
        }
      } else {
        const payload: UpdateIntegrationsPayload = {
          financial: {
            product: null,
            externalInstanceId: null,
          },
          hr: {
            product: (draft.hr.product || null) as HRProduct | null,
            externalInstanceId: draft.hr.instanceId || null,
            instanceName: draft.hr.instanceName || null,
          },
          project: {
            product: (draft.project.product || null) as ProjectProduct | null,
            externalInstanceId: draft.project.instanceId || null,
            instanceName: draft.project.instanceName || null,
            externalInstanceIdSecondary: draft.project.secondaryId || null,
          },
          planner: {
            product: null,
            externalInstanceId: null,
          },
          timesheet: {
            product: (draft.timesheet.product || null) as TimesheetProduct | null,
            externalInstanceId: draft.timesheet.instanceId || null,
            instanceName: draft.timesheet.instanceName || null,
          },
        };

        const updated = await integrationsService.updateIntegrations(activeCompany.id, payload);
        if (updated) {
          const preserved = { ...updated, userCount: currentUserCount };
          latestCompany = preserved;
          setActiveCompany(preserved);
          onCompanyUpdate(preserved, { suppressEffects: true });
          setDraft(buildDraftFromCompany(preserved));
        }
      }

      // 3. Persist pending user additions (single bulk request)
      if (pendingUserAdditions.length > 0) {
        await svc.addUsersToCompany(
          activeCompany.id,
          pendingUserAdditions.map((u) => u.id)
        );
      }

      // 4. Persist pending user removals (single bulk request)
      if (pendingUserRemovals.size > 0) {
        await svc.removeUsersFromCompany(
          activeCompany.id,
          Array.from(pendingUserRemovals)
        );
      }

      // Push final company state with correct user count to the parent table
      onCompanyUpdate({ ...latestCompany, userCount: currentUserCount });

      const hadUserChanges = pendingUserAdditions.length > 0 || pendingUserRemovals.size > 0;
      setPendingUserAdditions([]);
      setPendingUserRemovals(new Set());
      setIsDirty(false);
      toast.success('Changes saved');

      // After saving user changes, reload the user list from the API so that
      // the Users tab reflects the persisted state. Previously the sheet would
      // close immediately, but clearing pending state before the refetch caused
      // a stale render (savedUsers was never updated).
      if (hadUserChanges) {
        setUserRefreshKey((k) => k + 1);
      }
    } catch {
      toast.error('Failed to save changes — please try again');
    } finally {
      setIsSaving(false);
    }
  }

  function handleUserCountChange(count: number) {
    if (!activeCompany) return;
    const updated = { ...activeCompany, userCount: count };
    setActiveCompany(updated);
    // Staged add/remove only updates the count optimistically. Suppress side
    // effects (audit log + company refetch) so no API call fires until the
    // user commits via "Save All Changes".
    onCompanyUpdate(updated, { suppressEffects: true });
  }

  // Handle live updates from test/sync operations within rows (no dirty state change)
  function handleRowSaved(updated: CompanyWithIntegrations) {
    setActiveCompany(updated);
    onCompanyUpdate(updated);
    if (!isDirty) {
      setDraft(buildDraftFromCompany(updated));
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col overflow-hidden">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                <Building2 className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <SheetTitle className="text-base leading-tight truncate">
                  {mode === 'create' ? 'Add New Company' : (activeCompany?.name ?? 'Company Details')}
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {mode === 'create' ? 'Fill in the details below to create a new company' : activeCompany?.code}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {mode === 'create' ? (
            <CreateCompanyForm
              existingCodes={existingCodes}
              onCreated={(created) => {
                onCompanyCreated?.(created);
                onOpenChange(false);
              }}
              onCancel={() => onOpenChange(false)}
              isDemo={isDemo}
            />
          ) : !activeCompany || !draft ? null : (
            <Tabs defaultValue="integrations" className="flex flex-col flex-1 min-h-0">
              {/* Tab bar */}
              <div className="px-6 pt-3 pb-0 border-b shrink-0">
                <TabsList className="h-9 w-full rounded-none bg-transparent p-0 gap-0 justify-start">
                  <TabsTrigger
                    value="integrations"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                  >
                    Integrations
                  </TabsTrigger>
                  <TabsTrigger
                    value="details"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                  >
                    Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="users"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 h-9 text-sm"
                  >
                    Users
                    <span className={cn('ml-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-medium h-4 min-w-4 px-1', activeCompany.userCount === 0 ? 'bg-muted/60 text-muted-foreground/60' : 'bg-muted text-muted-foreground')}>
                      {activeCompany.userCount}
                    </span>
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Dirty state warning banner */}
              {(isDirty || hasUserChanges) && (
                <div className="flex items-center gap-2 px-6 py-2 bg-warning-50 dark:bg-warning-950/30 border-b border-warning-200 dark:border-warning-800 shrink-0">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning-600 shrink-0" />
                  <p className="text-xs text-warning-700 dark:text-warning-300 flex-1">
                    You have unsaved changes. Use <strong>Save All Changes</strong> below to commit them.
                  </p>
                </div>
              )}

              {/* ── Tab: Integrations ── */}
              <TabsContent value="integrations" className="flex-1 overflow-y-auto mt-0">
                {!canViewIntegrationsProp ? (
                  <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
                    <Lock className="h-8 w-8 text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground">You do not have permission</p>
                    <p className="text-xs text-muted-foreground">
                      You don't have permission to view integrations for this company.
                    </p>
                  </div>
                ) : (
                <div className="px-6 py-5 space-y-4 pb-8">
                  {/* Health summary */}
                  <IntegrationHealthSummary company={activeCompany} />

                  {/* Project group */}
                  <div className="rounded-lg border border-border bg-muted/20">
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border rounded-t-lg">
                      <span className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
                        Project
                      </span>
                    </div>
                    <div className="divide-y divide-border">
                      <div className="px-4 py-0">
                        <IntegrationRow
                          companyId={activeCompany.id}
                          type="project"
                          draft={draft.project}
                          savedStatus={activeCompany.projectIntegration.status}
                          savedLastSyncAt={activeCompany.projectIntegration.lastSyncAt}
                          onDraftChange={handleIntegrationDraftChange}
                          onSaved={handleRowSaved}
                          readOnly={!canManageIntegrationsProp}
                          isDemo={isDemo}
                        />
                      </div>
                    </div>
                  </div>

                  {/* HR group */}
                  <div className="rounded-lg border border-border bg-muted/20">
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border rounded-t-lg">
                      <span className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
                        HR
                      </span>
                    </div>
                    <div className="px-4 py-0">
                      <IntegrationRow
                        companyId={activeCompany.id}
                        type="hr"
                        draft={draft.hr}
                        savedStatus={activeCompany.hrIntegration.status}
                        savedLastSyncAt={activeCompany.hrIntegration.lastSyncAt}
                        onDraftChange={handleIntegrationDraftChange}
                        onSaved={handleRowSaved}
                        readOnly={!canManageIntegrationsProp}
                        isDemo={isDemo}
                      />
                    </div>
                  </div>
                </div>
                )}
              </TabsContent>

              {/* ── Tab: Details ── */}
              <TabsContent value="details" className="flex-1 overflow-y-auto mt-0">
                <div className="px-6 py-5 space-y-5">
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="company-name">Company Name</Label>
                      <Input
                        id="company-name"
                        value={draft.companyName}
                        onChange={(e) => updateDraftField('companyName', e.target.value)}
                        disabled={!canManageIntegrationsProp}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="company-code">Company Code</Label>
                      <Input
                        id="company-code"
                        value={draft.companyCode}
                        onChange={(e) => updateDraftField('companyCode', e.target.value.toUpperCase())}
                        className="uppercase"
                        disabled={!canManageIntegrationsProp}
                      />
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <div>
                        <Label htmlFor="company-status" className="text-sm font-medium">Active</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">Inactive companies are hidden from the company switcher</p>
                      </div>
                      <Switch
                        id="company-status"
                        checked={draft.isActive}
                        onCheckedChange={(checked) => updateDraftField('isActive', checked)}
                        disabled={!canManageIntegrationsProp}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ── Tab: Users ── */}
              <TabsContent value="users" className="flex-1 overflow-y-auto mt-0">
                <div className="px-6 py-5 pb-8">
                  <UsersWithAccessSection
                    company={activeCompany}
                    onUserCountChange={handleUserCountChange}
                    isDemo={isDemo}
                    canManage={canManageIntegrationsProp}
                    pendingAdditions={pendingUserAdditions}
                    pendingRemovals={pendingUserRemovals}
                    onPendingAdditionsChange={setPendingUserAdditions}
                    onPendingRemovalsChange={setPendingUserRemovals}
                    refreshKey={userRefreshKey}
                  />
                </div>
              </TabsContent>

              {/* ── Sticky footer — single Save All Changes button ── */}
              <div className="border-t px-6 py-3 shrink-0 flex items-center justify-between gap-3 bg-background">
                {(isDirty || hasUserChanges) ? (
                  <p className="text-xs text-muted-foreground">Unsaved changes</p>
                ) : (
                  <p className="text-xs text-muted-foreground">All changes saved</p>
                )}
                <Button
                  onClick={handleSaveAll}
                  disabled={
                    isSaving ||
                    (!isDirty && !hasUserChanges) ||
                    !canManageIntegrationsProp
                  }
                  size="sm"
                  className="gap-1.5"
                >
                  {isSaving ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</>
                  ) : (
                    <><Save className="h-3.5 w-3.5" />Save All Changes</>
                  )}
                </Button>
              </div>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      {/* Discard changes confirmation */}
      <DiscardChangesDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        onConfirm={handleDiscard}
      />
    </>
  );
}
